import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCheck, faChevronDown, faDownload, faEnvelope, faPaperPlane, faPlus, faRotateLeft, faSpinner, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { CommunicationService, sharedApiClient } from '../../fetcher';
import { CustomDropdown, IdentityService, OrbitSwap, StatusBadge, useAuth, useNotifications } from '@medprompt/shared';
import { LmcContractViolationEnum } from '../../enums/lmcContractViolationEnum';
import { QUALITY_ALERT_FORMS } from './qualityAlertForms';
import { adviseQualityAlert } from './qualityAlertAdvice';
import { downloadQualityAlert, emailQualityAlert } from './qualityAlertDownload';
import useDocumentRecord from '../useDocumentRecord';
import useDocumentPatient from '../useDocumentPatient';
import MissingDocumentView, { DOCUMENT_MISSING_REASON, DocumentLoadingView } from '../MissingDocumentView';
import '@medprompt/shared/src/common/modals.css';
import '../tabs/MessagingModals.css';
import './QualityAlertViewController.css';

const storageKeyFor = (documentId, templateId) => `medprompt_quality_alert_${documentId}_${templateId}`;

const todayLabel = () => new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

const exactTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const relativeTime = (iso, now) => {
  if (!iso) return 'Earlier';
  const minutes = Math.floor(Math.max(0, now - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const QualityAlertViewController = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const record = useDocumentRecord({ documentId, user });
  const patient = useDocumentPatient({
    documentId,
    doc: record.doc,
    documentService: record.documentService,
    docScopeOptions: record.docScopeOptions,
    loadKey: record.loadKey,
  });
  const doc = record.doc;
  const patientData = patient.patientData;
  const communicationService = useMemo(() => new CommunicationService(sharedApiClient), []);
  const identityService = useMemo(() => new IdentityService(), []);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [prompt, setPrompt] = useState('');
  const [badges, setBadges] = useState([]);
  const [messages, setMessages] = useState([]);
  const [fields, setFields] = useState({});
  const [contextOverrides, setContextOverrides] = useState({});
  const [clinic, setClinic] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadedKey, setLoadedKey] = useState('');
  const [pinnedIndex, setPinnedIndex] = useState(null);
  const [formMenuOpen, setFormMenuOpen] = useState(false);
  const [violationsOpen, setViolationsOpen] = useState(false);
  const promptRef = useRef(null);
  const [now, setNow] = useState(() => Date.now());

  const template = templates.find((item) => item.templateId === templateId);
  const FormCard = QUALITY_ALERT_FORMS[templateId];

  const baseContext = useMemo(() => ({
    completedOn: todayLabel(),
    nhsNumber: patientData?.nhsId || doc?.nhsId || '',
    patientName: patientData?.name || '',
    dateOfBirth: patientData?.dateOfBirth || '',
    practiceName: clinic?.clinicName || '',
    odsCode: clinic?.odsCode || '',
    raiserName: user?.name || '',
    practicePhone: '',
    practiceEmail: '',
    gmcNumber: '',
    reporterName: '',
  }), [patientData, doc, clinic, user]);

  const context = { ...baseContext, ...contextOverrides };

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    communicationService.fetchQualityAlertTemplates()
      .then((loaded) => {
        if (cancelled) return;
        const list = loaded || [];
        setTemplates(list);
        setTemplateId((current) => current || list[0]?.templateId || '');
      })
      .catch(() => addNotification('qa-templates', 'Could not load quality alert forms', { isError: true }));
    identityService.fetchClinics()
      .then((clinics) => {
        if (cancelled) return;
        const selectedId = sessionStorage.getItem('selectedClinicId');
        setClinic(clinics?.find((item) => item.clinicId === selectedId) || clinics?.[0] || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [communicationService, identityService, addNotification]);

  useEffect(() => {
    if (!templateId || !documentId) return;
    const key = storageKeyFor(documentId, templateId);
    if (loadedKey === key) return;
    try {
      const saved = JSON.parse(sessionStorage.getItem(key) || 'null');
      setMessages(saved?.messages || []);
      setFields(saved?.fields || {});
      setBadges(saved?.badges || []);
      setContextOverrides(saved?.contextOverrides || {});
      setPrompt(saved?.prompt || '');
      setPinnedIndex(null);
    } catch {
      setMessages([]);
      setFields({});
      setBadges([]);
      setContextOverrides({});
    }
    setLoadedKey(key);
  }, [templateId, documentId, loadedKey]);

  useEffect(() => {
    if (!templateId || !documentId || loadedKey !== storageKeyFor(documentId, templateId)) return;
    sessionStorage.setItem(storageKeyFor(documentId, templateId), JSON.stringify({
      messages, fields, badges, contextOverrides, prompt,
    }));
  }, [messages, fields, badges, contextOverrides, prompt, templateId, documentId, loadedKey]);

  const violationOptions = useMemo(() => {
    const flagged = new Set(doc?.lmcContractViolations || []);
    return Object.values(LmcContractViolationEnum)
      .sort((a, b) => Number(flagged.has(b.value)) - Number(flagged.has(a.value)));
  }, [doc]);

  const toggleBadge = (value) => {
    setBadges((current) => (
      current.includes(value) ? current.filter((item) => item !== value) : [...current, value]
    ));
  };

  const history = messages.filter((message) => message.type === 'assistant' && !message.error).length
    ? messages.reduce((turns, message, index) => {
      if (message.type !== 'user') return turns;
      const reply = messages.slice(index + 1).find((item) => item.type === 'assistant');
      if (!reply || reply.error) return turns;
      turns.push({ prompt: message.prompt, violations: message.violations, message: reply.message });
      return turns;
    }, [])
    : [];

  const handleGenerate = async () => {
    if (!template || isGenerating) return;
    const userPrompt = prompt.trim();
    const sentBadges = [...badges];
    const sentAt = new Date().toISOString();
    setMessages((current) => [...current, { type: 'user', prompt: userPrompt, violations: sentBadges, at: sentAt }]);
    setPrompt('');
    setIsGenerating(true);
    try {
      const response = await communicationService.generateQualityAlert(documentId, {
        templateId: template.templateId,
        contractViolations: sentBadges,
        prompt: userPrompt,
        fields,
        history,
      });
      if (response?.error) {
        setMessages((current) => [...current, { type: 'assistant', error: response.error, message: response.error, at: new Date().toISOString() }]);
      } else {
        const nextFields = response.fields || {};
        setFields(nextFields);
        setPinnedIndex(null);
        setMessages((current) => [...current, {
          type: 'assistant',
          message: response.message || '',
          fields: nextFields,
          advice: '',
          at: new Date().toISOString(),
        }]);
      }
    } catch (error) {
      setMessages((current) => [...current, { type: 'assistant', error: 'Failed to generate the quality alert', message: '', at: new Date().toISOString() }]);
    } finally {
      setIsGenerating(false);
    }
  };

  const updateLatestFields = (key, value) => {
    setFields((current) => ({ ...current, [key]: value }));
    setMessages((current) => {
      const next = [...current];
      for (let index = next.length - 1; index >= 0; index -= 1) {
        if (next[index].type === 'assistant' && next[index].fields) {
          next[index] = { ...next[index], fields: { ...next[index].fields, [key]: value } };
          break;
        }
      }
      return next;
    });
  };

  const onContext = (key, value) => setContextOverrides((current) => ({ ...current, [key]: value }));

  const revertTo = (index) => {
    const message = messages[index];
    if (!message?.fields || index === latestIndex) return;
    const nextFields = { ...message.fields };
    setFields(nextFields);
    setMessages((current) => current.slice(0, index + 1));
    setPinnedIndex(null);
  };

  const handleDownload = () => {
    if (!template) return;
    downloadQualityAlert(template, shownFields, context);
  };

  const handleEmail = () => {
    if (!template) return;
    if (!emailQualityAlert(template, shownFields, context)) {
      addNotification('qa-email', 'Choose a destination on the form before emailing', { isError: true });
    }
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleGenerate();
    }
  };

  const fitPrompt = (field) => {
    if (!field) return;
    field.style.height = 'auto';
    const style = window.getComputedStyle(field);
    const line = parseFloat(style.lineHeight) || 21;
    const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const max = line * 4 + padding;
    field.style.height = `${Math.min(field.scrollHeight, max)}px`;
    field.style.overflowY = field.scrollHeight > max + 1 ? 'auto' : 'hidden';
  };

  useEffect(() => {
    fitPrompt(promptRef.current);
  }, [prompt]);

  const versions = messages
    .map((message, index) => ({ message, index }))
    .filter((item) => item.message.type === 'assistant' && item.message.fields);
  const latestIndex = versions.length ? versions[versions.length - 1].index : null;
  const shownIndex = pinnedIndex != null && messages[pinnedIndex]?.fields ? pinnedIndex : latestIndex;
  const shown = shownIndex == null ? null : messages[shownIndex];
  const isLatest = shownIndex != null && shownIndex === latestIndex;
  const shownFields = isLatest ? fields : (shown?.fields || {});
  const shownAdvice = template ? adviseQualityAlert(template, shownFields, context.nhsNumber) : '';
  const chatStarted = messages.length > 0 || isGenerating;

  const formPicker = (
    <div className="qa-picker">
      <span className="section-label">Quality alert form</span>
      {templates.length === 0 ? (
        <p className="qa-empty">No quality alert forms are loaded.</p>
      ) : (
        <CustomDropdown
          selected={template ? `${template.displayName} · ${template.icbName}` : 'Choose a form'}
          options={templates.map((item) => ({
            value: item.templateId,
            display: `${item.displayName} · ${item.icbName}`,
          }))}
          isOpen={formMenuOpen}
          toggleDropdown={() => setFormMenuOpen((open) => !open)}
          onSelect={(option) => {
            setLoadedKey('');
            setPinnedIndex(null);
            setTemplateId(option.value);
            setFormMenuOpen(false);
          }}
          width="100%"
        />
      )}
    </div>
  );

  const showViolations = !chatStarted || violationsOpen;

  const composer = (
    <div className="qa-composer">
      {chatStarted ? (
        <button
          type="button"
          className="qa-violations-toggle"
          aria-expanded={violationsOpen}
          onClick={() => setViolationsOpen((open) => !open)}
        >
          <span>Contract violations{badges.length ? ` (${badges.length})` : ''}</span>
          <FontAwesomeIcon icon={faChevronDown} className={violationsOpen ? 'is-open' : ''} />
        </button>
      ) : (
        <span className="section-label">Contract violations</span>
      )}
      {showViolations ? <div className="qa-badge-row">
        {violationOptions.map((violation) => {
          const on = badges.includes(violation.value);
          const flagged = doc?.lmcContractViolations?.includes(violation.value);
          return (
            <StatusBadge
              key={violation.value}
              label={(
                <span className="qa-violation-label">
                  <FontAwesomeIcon icon={on ? faCheck : faPlus} />
                  {violation.displayName}
                </span>
              )}
              colorFamily={on ? 'green' : flagged ? 'yellow' : 'gray'}
              size="small"
              tooltip={(
                <>
                  <span className="risk-category-tooltip-lead">
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    {violation.displayName}
                  </span>
                  <span className="risk-category-tooltip-hint">{violation.description}</span>
                </>
              )}
              className="qa-violation-badge"
              onClick={() => toggleBadge(violation.value)}
            />
          );
        })}
      </div> : null}
      <div className="qa-prompt-shell">
        <textarea
          ref={promptRef}
          value={prompt}
          onChange={(event) => {
            setPrompt(event.target.value);
            fitPrompt(event.target);
          }}
          onKeyDown={onKeyDown}
          placeholder={chatStarted ? 'Reply' : 'Describe what to raise, or leave this blank'}
          rows={1}
          disabled={isGenerating || !template}
        />
        <button type="button" className="qa-send-arrow" onClick={handleGenerate} disabled={isGenerating || !template} aria-label={messages.length === 0 ? 'Draft' : 'Send'}>
          <FontAwesomeIcon icon={isGenerating ? faSpinner : faPaperPlane} className={isGenerating ? 'fa-spin' : ''} />
        </button>
      </div>
    </div>
  );

  if (record.loading) {
    return <DocumentLoadingView />;
  }

  if (!doc) {
    return (
      <MissingDocumentView
        reason={record.missingReason || DOCUMENT_MISSING_REASON.NEVER_EXISTED}
        backLabel="Back to Documents"
        onBack={() => navigate('/')}
        documentId={documentId}
        activeUsers={record.activeUsers}
        teams={record.teams}
        showAuditTrail={record.showMissingAuditModal}
        onViewAuditTrail={record.openMissingAuditTrail}
        onCloseAuditTrail={record.closeMissingAuditTrail}
        onRetry={record.retryLoad}
      />
    );
  }

  return (
    <div className="qa-page">
      <header className="qa-topbar">
        <button type="button" className="qa-back" onClick={() => navigate(`/document-detail/${documentId}`)}>
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Back to document</span>
        </button>
        <h1>Raise quality alert</h1>
      </header>
        {chatStarted ? (
        <div className="qa-split">
          <nav className="qa-versions" aria-label="Versions">
            {versions.map(({ message, index }) => (
              <button
                key={index}
                type="button"
                className={`qa-version-tab${index === shownIndex ? ' is-active' : ''}`}
                aria-current={index === shownIndex ? 'page' : undefined}
                title={exactTime(message.at) || undefined}
                onClick={() => setPinnedIndex(index === latestIndex ? null : index)}
              >
                {relativeTime(message.at, now)}
              </button>
            ))}
          </nav>
          <section className="qa-doc">
            {formPicker}
            <div className="qa-doc-scroll">
              {!shown ? (
                <div className="patient-info-section qa-empty-card">
                  <div className="patient-name">Drafting the quality alert</div>
                  <p>The form will appear here.</p>
                </div>
              ) : FormCard ? (
                <FormCard
                  template={template}
                  fields={shownFields}
                  context={context}
                  advice={shownAdvice}
                  readOnly={!isLatest}
                  onField={isLatest ? updateLatestFields : () => {}}
                  onContext={isLatest ? onContext : () => {}}
                />
              ) : (
                <p className="qa-empty">This form has no presenter yet.</p>
              )}
            </div>
            {shown ? (
              <div className="qa-doc-footer">
                {!isLatest ? <span className="qa-readonly">Viewing an earlier version</span> : <span />}
                <button type="button" className="qa-pill" onClick={handleDownload}>
                  <FontAwesomeIcon icon={faDownload} /> Download
                </button>
                <button type="button" className="send-button" onClick={handleEmail}>
                  <FontAwesomeIcon icon={faEnvelope} /> Email
                </button>
              </div>
            ) : null}
          </section>
          <aside className="qa-chat">
            <div className="qa-thread">
              {messages.map((message, index) => (
                message.type === 'user' ? (
                  <div key={`${message.type}-${index}`} className="qa-msg qa-msg-user">
                    {message.violations?.length ? (
                      <div className="qa-badge-row">
                        {message.violations.map((value) => (
                          <StatusBadge
                            key={value}
                            label={LmcContractViolationEnum[value]?.displayName || value}
                            colorFamily="green"
                            size="small"
                          />
                        ))}
                      </div>
                    ) : null}
                    <div className="qa-user-pill">{message.prompt || 'Draft from the document'}</div>
                    <span className="qa-msg-time" title={exactTime(message.at) || undefined}>{relativeTime(message.at, now)}</span>
                  </div>
                ) : (
                  <div key={`${message.type}-${index}`} className="qa-msg qa-msg-assistant">
                    {message.error ? <p className="qa-error">{message.error}</p> : <p className="qa-reply">{message.message}</p>}
                    <div className="qa-msg-actions">
                      {message.fields && index !== latestIndex ? (
                        <button type="button" className="qa-revert" onClick={() => revertTo(index)} aria-label="Revert to this draft" title="Revert to this draft">
                          <FontAwesomeIcon icon={faRotateLeft} />
                        </button>
                      ) : null}
                      <span className="qa-msg-time" title={exactTime(message.at) || undefined}>{relativeTime(message.at, now)}</span>
                    </div>
                  </div>
                )
              ))}
              {isGenerating ? (
                <div className="qa-drafting" role="status">
                  <OrbitSwap phrases={['Drafting', 'Writing']} />
                </div>
              ) : null}
            </div>
            {composer}
          </aside>
        </div>
        ) : (
          <div className="qa-start">
            <div className="qa-start-inner">
              <h3>Draft a quality alert</h3>
              <p>Choose the form, tick any contract violations, then draft. You can leave the box blank and the alert will be written from this document. The filled form opens beside the chat.</p>
              {formPicker}
              {composer}
            </div>
          </div>
        )}
    </div>
  );
};

export default QualityAlertViewController;
